import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { applyLicenseKey } from "../../../../../../utils/License";
import ConfirmationModel from "../../../../../../global/components/ConfirmationModal/confirmationModel";
import {
  addProjectFilterMeta,
  deleteProjectFilterMeta,
  getProjectFilterMeta,
  syncProjectFilterMetaFromProjects,
} from "./Service";
import MetaDrawerComponent from "./MetaDrawerComponent";
import {
  openErrorNotification,
  openSuccessNotification,
} from "../../../../../../helpers/methods";
import YhInput from "../../../../../../ui/YhInput";
import YhButton from "../../../../../../ui/YhButton";
import YhTable, {
  YhTableColumn,
  YhTableCellParams,
} from "../../../../../../ui/YhTable";
import { selectActiveOrganizationId } from "../../../../../../redux/authSlice";

const TAB_LIST = [
  { label: "Property Purpose", key: "propertyPurpose" },
  { label: "Building Type", key: "buildingType" },
  { label: "Property Type", key: "propertyType" },
  { label: "Bedrooms", key: "bedrooms" },
  { label: "Price Range", key: "priceRange" },
  { label: "Property Status", key: "propertyStatus" },
  { label: "Sale Type", key: "propertySaleType" },
  { label: "Possession", key: "expectedPossession" },
  { label: "Furnishing Type", key: "furnishingType" },
  { label: "Highlights", key: "highlights" },
  { label: "Specifications", key: "specifications" },
  { label: "Amenities", key: "amenities" },
];

const FIELD_LABELS: Record<string, string> = {
  propertyPurpose: "Property Purpose",
  propertyType: "Property Type",
  buildingType: "Building Type",
  tags: "Tags",
  propertyStatus: "Property Status",
  all: "All projects",
};

const ProjectFilterMetaData = () => {
  const activeOrganizationId = useSelector(selectActiveOrganizationId);
  const orgId =
    activeOrganizationId &&
    activeOrganizationId !== "all" &&
    String(activeOrganizationId).trim()
      ? String(activeOrganizationId)
      : "";

  const [tabIndex, setTabIndex] = useState(0);
  const [search, setSearch] = useState("");
  const [allMeta, setAllMeta] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [metaDrawerOpen, setMetaDrawerOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number } | null>(
    null
  );
  const menuRef = useRef<HTMLDivElement | null>(null);

  const currentTab = TAB_LIST[tabIndex];
  const isPurposeTab = currentTab.key === "propertyPurpose";

  useEffect(() => {
    applyLicenseKey();
  }, []);

  useEffect(() => {
    if (!metaDrawerOpen) {
      setSelectedRow(null);
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, metaDrawerOpen, tabIndex, orgId]);

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const handleSyncFromProjects = async () => {
    if (!orgId) {
      openErrorNotification("Select an organization first");
      return;
    }
    try {
      setSyncing(true);
      const response = await syncProjectFilterMetaFromProjects();
      const added = response?.data?.added || {};
      const totalAdded = Object.values(added).reduce(
        (sum: number, n: any) => sum + Number(n || 0),
        0
      );
      openSuccessNotification(
        totalAdded
          ? `Added ${totalAdded} filter values from projects`
          : response?.message || "Filters are already in sync with project data"
      );
      fetchData();
    } catch (error: any) {
      console.error("Error syncing filters:", error);
      openErrorNotification(error?.message || "Could not sync from projects");
    } finally {
      setSyncing(false);
    }
  };

  const fetchData = async () => {
    if (!orgId) {
      setAllMeta([]);
      return;
    }
    try {
      setLoading(true);
      const response = await getProjectFilterMeta(currentTab.key, search);
      setAllMeta(response.data || []);
    } catch (error) {
      console.error("Error fetching metadata:", error);
    } finally {
      setLoading(false);
    }
  };

  const getCurrentRows = () => {
    const rows = allMeta?.[0]?.[currentTab.key] || [];
    return rows.map((row: any, index: number) => ({
      ...row,
      sno: index + 1,
    }));
  };

  const patchRow = async (row: any, patch: Record<string, any>, message: string) => {
    try {
      const currentArray = allMeta[0]?.[currentTab.key] || [];
      const rowId = String(row?._id || "");
      const updatedArray = currentArray.map((item: any) => {
        const plain = JSON.parse(JSON.stringify(item));
        if (String(plain?._id || "") !== rowId) return plain;
        return { ...plain, ...patch };
      });

      const payload = {
        _id: allMeta[0]?._id,
        [currentTab.key]: updatedArray,
      };

      const addResponse = await addProjectFilterMeta(payload);
      if (addResponse) {
        fetchData();
        openSuccessNotification(addResponse?.message || message);
      }
    } catch (error) {
      console.error("Error submitting filter meta:", error);
      openErrorNotification("Could not update this filter");
    }
  };

  const handleStatus = async (row: any, checked: boolean) => {
    await patchRow(row, { status: checked }, "Status updated successfully");
  };

  const handleShowOnWebsite = async (row: any, checked: boolean) => {
    await patchRow(
      row,
      { showOnWebsite: checked },
      "Website visibility updated"
    );
  };

  const handleDelete = async () => {
    const id = allMeta[0]?._id;
    if (!id) return;
    try {
      const payload = {
        category: [currentTab.key],
        metaId: selectedRow?._id,
      };
      const response = await deleteProjectFilterMeta(id, payload);
      if (response.success === 1) {
        openSuccessNotification(response.message);
        fetchData();
      }
    } catch (error) {
      console.error("Error deleting filter meta:", error);
    } finally {
      setDialogOpen(false);
      setSelectedRow(null);
    }
  };

  const closeDrawer = () => {
    setMetaDrawerOpen(false);
    setSelectedRow(null);
  };

  const columns: YhTableColumn[] = [
    {
      headerName: "S.No.",
      field: "sno",
      minWidth: 70,
      renderCell: (params: YhTableCellParams) => (
        <span className="text-sm text-[#6B7280]">{params.row?.sno}</span>
      ),
    },
    ...(currentTab.key === "amenities"
      ? [
          {
            headerName: "Category Name",
            field: "categoryName",
            minWidth: 160,
            renderCell: (params: YhTableCellParams) => (
              <span className="text-sm text-[#374151]">
                {params.row?.categoryName || "—"}
              </span>
            ),
          } as YhTableColumn,
        ]
      : []),
    {
      headerName: "Name",
      field: "name",
      minWidth: 180,
      flex: 1,
      renderCell: (params: YhTableCellParams) => (
        <span className="text-sm font-semibold capitalize text-[#111827]">
          {params.row?.name || "—"}
        </span>
      ),
    },
    ...(isPurposeTab
      ? [
          {
            headerName: "Field",
            field: "filterField",
            minWidth: 140,
            renderCell: (params: YhTableCellParams) => (
              <span className="text-sm text-[#374151]">
                {FIELD_LABELS[params.row?.filterField] ||
                  params.row?.filterField ||
                  "Property Purpose"}
              </span>
            ),
          } as YhTableColumn,
          {
            headerName: "Filter value",
            field: "filterValue",
            minWidth: 140,
            renderCell: (params: YhTableCellParams) => (
              <span className="text-sm text-[#374151]">
                {params.row?.uiType === "projects"
                  ? "All projects"
                  : params.row?.filterValue || params.row?.name || "—"}
              </span>
            ),
          } as YhTableColumn,
          {
            headerName: "UI",
            field: "uiType",
            minWidth: 120,
            renderCell: (params: YhTableCellParams) => (
              <span className="text-sm text-[#374151]">
                {params.row?.uiType === "projects"
                  ? "Projects gallery"
                  : "Standard"}
              </span>
            ),
          } as YhTableColumn,
        ]
      : []),
    {
      headerName: "Icon",
      field: "icon",
      minWidth: 100,
      renderCell: (params: YhTableCellParams) =>
        params.row?.icon ? (
          <a href={params.row.icon} target="_blank" rel="noopener noreferrer">
            <img
              src={params.row.icon}
              alt="icon"
              className="h-10 w-10 rounded object-cover"
            />
          </a>
        ) : (
          <span className="text-xs text-[#9CA3AF]">No Image</span>
        ),
    },
    {
      headerName: "Status",
      field: "status",
      minWidth: 110,
      renderCell: (params: YhTableCellParams) => (
        <label
          className="relative inline-flex h-6 w-11 cursor-pointer items-center"
          onClick={(e) => e.stopPropagation()}
        >
          <input
            type="checkbox"
            className="peer sr-only"
            checked={!!params.row.status}
            onChange={(e) => handleStatus(params.row, e.target.checked)}
          />
          <span className="absolute inset-0 rounded-full bg-[#D1D5DB] transition peer-checked:bg-[#A02448]" />
          <span className="absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow transition peer-checked:translate-x-5" />
        </label>
      ),
    },
    ...(isPurposeTab
      ? [
          {
            headerName: "Website",
            field: "showOnWebsite",
            minWidth: 110,
            renderCell: (params: YhTableCellParams) => (
              <label
                className="relative inline-flex h-6 w-11 cursor-pointer items-center"
                onClick={(e) => e.stopPropagation()}
              >
                <input
                  type="checkbox"
                  className="peer sr-only"
                  checked={params.row.showOnWebsite !== false}
                  onChange={(e) =>
                    handleShowOnWebsite(params.row, e.target.checked)
                  }
                />
                <span className="absolute inset-0 rounded-full bg-[#D1D5DB] transition peer-checked:bg-[#A02448]" />
                <span className="absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow transition peer-checked:translate-x-5" />
              </label>
            ),
          } as YhTableColumn,
        ]
      : []),
    {
      headerName: "Actions",
      field: "actions",
      minWidth: 90,
      renderCell: (params: YhTableCellParams) => (
        <button
          type="button"
          data-action-button="true"
          className="flex h-8 w-8 items-center justify-center rounded-lg text-[#6B7280] hover:bg-[#F3F4F6]"
          onClick={(e) => {
            e.stopPropagation();
            const rect = (
              e.currentTarget as HTMLButtonElement
            ).getBoundingClientRect();
            setMenuPos({ top: rect.bottom + 4, left: rect.left - 160 });
            setSelectedRow(params.row);
            setMenuOpen(true);
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <circle cx="12" cy="5" r="1.5" />
            <circle cx="12" cy="12" r="1.5" />
            <circle cx="12" cy="19" r="1.5" />
          </svg>
        </button>
      ),
    },
  ];

  return (
    <div className="flex h-[calc(100vh-68px)] max-h-[calc(100vh-68px)] flex-col overflow-hidden bg-white p-4 sm:p-5 lg:p-6">
      <div className="mb-4 flex shrink-0 flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-[#1E1B4B] sm:text-3xl">
            Project Filter List
          </h1>
          <p className="mt-1 text-sm text-[#6B7280]">
            Organization-wise filters built from values that already exist on
            projects. Use Sync to fill Property Type, Bedrooms, Price Range,
            and the other tabs from live project data.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <YhButton
            variant="secondary"
            size="sm"
            disabled={!orgId || syncing}
            onClick={handleSyncFromProjects}
          >
            {syncing ? "Syncing…" : "Sync from projects"}
          </YhButton>
          <YhButton
            variant="primary"
            size="sm"
            disabled={!orgId}
            onClick={() => {
              setSelectedRow(null);
              setMetaDrawerOpen(true);
            }}
          >
            + Create {currentTab.label}
          </YhButton>
        </div>
      </div>

      {!orgId ? (
        <div className="mb-3 shrink-0 rounded-xl border border-[#FDE68A] bg-[#FFFBEB] px-4 py-3 text-sm text-[#92400E]">
          Select a specific organization (not “All”) to view and manage project
          filters for that org. The public website uses the default YHATAW
          organization.
        </div>
      ) : null}

      <div className="mb-3 shrink-0 overflow-x-auto">
        <div className="inline-flex min-w-full gap-1 rounded-xl border border-[#EEF0F5] bg-white p-1">
          {TAB_LIST.map((tab, idx) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => {
                setTabIndex(idx);
                setSearch("");
              }}
              className={`whitespace-nowrap rounded-lg px-3 py-2 text-xs font-medium transition sm:text-sm ${
                tabIndex === idx
                  ? "bg-[#A02448] text-white shadow-sm"
                  : "text-[#6B7280] hover:bg-[#F8FAFC]"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className="mb-3 shrink-0 sm:max-w-[420px]">
        <YhInput
          placeholder={`Search ${currentTab.label.toLowerCase()}…`}
          value={search}
          type="search"
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div className="relative min-h-0 flex-1 overflow-hidden">
        <YhTable
          columns={columns}
          rows={orgId ? getCurrentRows() : []}
          getRowId={(row) => row?._id || row?.name}
          loading={loading}
          emptyMessage={
            orgId
              ? `No ${currentTab.label.toLowerCase()} found`
              : "No organization selected"
          }
          className="h-full"
          showPagination={false}
        />
      </div>

      {menuOpen && menuPos ? (
        <div
          ref={menuRef}
          className="fixed z-[1500] w-48 overflow-hidden rounded-xl border border-[#EEF0F5] bg-white py-1 shadow-[0_8px_24px_rgba(15,23,42,0.12)]"
          style={{ top: menuPos.top, left: menuPos.left }}
        >
          <p className="px-3 py-2 text-xs font-semibold uppercase text-[#9CA3AF]">
            Actions
          </p>
          <button
            type="button"
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-[#111827] hover:bg-[#F8FAFC]"
            onClick={() => {
              setMenuOpen(false);
              setMetaDrawerOpen(true);
            }}
          >
            Edit
          </button>
          <button
            type="button"
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-[#DC2626] hover:bg-[#FEF2F2]"
            onClick={() => {
              setDialogOpen(true);
              setMenuOpen(false);
            }}
          >
            Delete
          </button>
        </div>
      ) : null}

      {metaDrawerOpen && orgId ? (
        <MetaDrawerComponent
          currentTabKey={currentTab.key}
          currentTabLabel={currentTab.label}
          selectedRow={selectedRow}
          search={search}
          closeDrawer={closeDrawer}
        />
      ) : null}

      <ConfirmationModel
        open={dialogOpen}
        title="Confirmation"
        description="Are you sure you want to delete?"
        onConfirm={handleDelete}
        onCancel={() => setDialogOpen(false)}
      />
    </div>
  );
};

export default ProjectFilterMetaData;
